import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { MaterialRadioGroupControl } from 'console/shared/components/fields';
import { useEditingProps } from '../../utils/unpack-props';
import { HeatingCircuit, Pza } from 'z3k-config';
import { useBox } from 'shared/hooks/utils';
import { hasOutputTemperatureSource } from 'z3k-parser/validation/z3k/config';
import { SettingFullRow } from 'console/device-settings/z3k/components/material-utils/settings-card/full-row';
import { SettingCard } from 'console/device-settings/z3k/components/material-utils/settings-card/card';
import { HeatingCircuitContext } from 'console/device-settings/z3k/components/heating-circuits/context/type';
import { PZAPane } from '../../utils/pza-pane';
import { Z3KSettings, Z3KSettingsEditor, } from 'console/device-settings/z3k/provider/types';
import { HelpIcon } from 'console/shared/components/fields/help-icon';
import { DeltaTemp } from 'z3k-settings/components/heating-circuits/additional-params/delta-temp';
import { findLinksToPzaAndRemove } from 'z3k-parser/lib/pza';
export const PzaFields = memo(function PzaFieldsImpl() {
    const { item, id, z3k_name, onChange } = useContext(HeatingCircuitContext);
    const hcBox = useBox(item);
    const { device_spec } = useContext(Z3KSettings);
    const { config, replace_config: onChangeConfig } = useContext(Z3KSettingsEditor);
    const configBox = useBox(config);
    const pzas = useMemo(() => Pza.get.all(config), [config]);
    const has_outside_temperature_source = useMemo(() => hasOutputTemperatureSource(config, true), [config]);
    const pzaEditingProps = useEditingProps(id, z3k_name, 'pza');
    const onChangePZA = useCallback((new_pza) => {
        if (new_pza == null) {
            return;
        }
        onChangeConfig(Pza.get.replaceById(configBox.current, new_pza.id, new_pza));
    }, [onChangeConfig]);
    const onAddPZA = useCallback((new_pza) => {
        const [newPzaId, newConfig] = Pza.get.add(configBox.current, new_pza);
        onChangeConfig(HeatingCircuit.get.applySetterById(newConfig, hcBox.current.id, {
            pza: newPzaId,
        }));
    }, [onChangeConfig]);
    const onRemovePZA = useCallback((pza_to_del) => onChangeConfig(findLinksToPzaAndRemove(HeatingCircuit.get.modifyById(configBox.current, hcBox.current.id, (_hc) => ({
        ...HeatingCircuit.setPZARequestOnlyMode(_hc, false),
        pza: 0,
    })), pza_to_del.id)), [onChangeConfig]);
    const onChangePZASelection = useCallback((value) => {
        onChangeConfig(HeatingCircuit.get.applySetterById(configBox.current, hcBox.current.id, {
            pza: value ?? 0,
        }));
    }, [onChangeConfig]);
    const pzaObject = useMemo(() => {
        if (!HeatingCircuit.hasPZA(item)) {
            return null;
        }
        return pzas.find(({ id }) => id === item.pza) ?? null;
    }, [pzaEditingProps.value, pzas]);
    const radio_control_values = useMemo(() => [
        {
            label: 'Регулирования температуры воздуха',
            value: '0',
            help_id: null,
        },
        {
            label: 'Запроса тепла',
            value: '1',
            help_id: null,
        },
    ], []);
    const radio_field = useCallback((input_props) => (_jsx(MaterialRadioGroupControl, { control_values: radio_control_values, ...input_props })), [radio_control_values]);
    return (HeatingCircuit.isBoiler(item) &&
        device_spec.heating.has_heating &&
        device_spec.heating.has_extended_heating.show_pza_for_boiler_circuit) ||
        HeatingCircuit.isConsumer(item) ? (_jsxs(SettingCard, { title: _jsxs(Box, { display: "flex", children: [_jsx(Typography, { variant: "h6", children: "\u041F\u043E\u0433\u043E\u0434\u043E\u0437\u0430\u0432\u0438\u0441\u0438\u043C\u0430\u044F \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u043A\u0430" }), _jsx(Box, { ml: 1, children: _jsx(HelpIcon, { help_id: "heating_circuits_pza" }) })] }), children: [_jsx(SettingFullRow, { children: _jsx(PZAPane, { value: pzaObject, onChange: onChangePZA, onAdd: onAddPZA, onRemove: onRemovePZA, onChangeSelection: onChangePZASelection, pzas: pzas, has_outside_temperature_source: has_outside_temperature_source, validation_result: pzaEditingProps.validation_result, validation_warnings_result: pzaEditingProps.validation_warnings_result }) }), _jsx(DeltaTemp, {})] })) : null;
});
